import React, { useEffect, useState, useMemo } from 'react'
import '../styles/Services.css'
import heroImage from '../assets/hero.png'
import qrcodeImage from '../assets/qrcode.png'
import reservationImage from '../assets/reservation.png'
import portfolioImage from '../assets/portfolio.png'
import { useTranslation } from '../context/LanguageContext.jsx'
import FormStatusModal from './FormStatusModal.jsx'
import { sendDemoEmail } from '../lib/emailjs.js'

const COMPACT_MQ = '(max-width: 768px)'

const SERVICE_ASSETS = [
  { image: portfolioImage },
  { image: heroImage },
  { image: qrcodeImage },
  { image: reservationImage },
]

const Services = () => {
  const { t, dict } = useTranslation()
  const [visibleCards, setVisibleCards] = useState([])
  const [isCompact, setIsCompact] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(COMPACT_MQ).matches
  )
  const [packageExpanded, setPackageExpanded] = useState(() => {
    if (typeof window === 'undefined') return true
    return !window.matchMedia(COMPACT_MQ).matches
  })
  const [serviceExpanded, setServiceExpanded] = useState({})
  const [demoSending, setDemoSending] = useState(false)
  const [demoStatus, setDemoStatus] = useState(null)

  const services = useMemo(
    () => dict.services.items.map((item, i) => ({ ...item, ...SERVICE_ASSETS[i] })),
    [dict.services.items]
  )

  const pkg = dict.services.package

  useEffect(() => {
    const mq = window.matchMedia(COMPACT_MQ)
    const sync = () => {
      const compact = mq.matches
      setIsCompact(compact)
      if (!compact) {
        setPackageExpanded(true)
        setServiceExpanded({})
      } else {
        setPackageExpanded(false)
      }
    }
    sync()
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          const raw = entry.target.getAttribute('data-card-index')
          const cardIndex = raw === 'package' ? 'package' : Number(raw)
          setVisibleCards((prev) => (prev.includes(cardIndex) ? prev : [...prev, cardIndex]))
          observer.unobserve(entry.target)
        })
      },
      { threshold: 0.2 }
    )

    const cards = document.querySelectorAll(
      '.service-card[data-card-index], .service-package-card[data-card-index]'
    )
    cards.forEach((card) => observer.observe(card))

    return () => observer.disconnect()
  }, [services])

  const handleDemoSubmit = async (e) => {
    e.preventDefault()
    if (demoSending) return

    const form = e.currentTarget
    const data = new FormData(form)
    const name = String(data.get('name') || '').trim()
    const email = String(data.get('email') || '').trim()
    const business = String(data.get('business') || '').trim()
    const phone = String(data.get('phone') || '').trim()
    const message = String(data.get('message') || '').trim()

    setDemoSending(true)
    try {
      await sendDemoEmail({ name, email, business, phone, message })
      form.reset()
      setDemoStatus('success')
    } catch (err) {
      console.error('Demo form EmailJS error:', err)
      setDemoStatus('error')
    } finally {
      setDemoSending(false)
    }
  }

  return (
    <section id="services" className="services">
      <div className="services-header">
        <h2>{t('services.title')}</h2>
        <p className="services-subtitle">{t('services.subtitle')}</p>
      </div>

      <div className="services-package-wrap">
        <article
          id="demo"
          data-card-index="package"
          className={`service-package-card ${
            visibleCards.includes('package') ? 'is-visible' : ''
          } ${isCompact ? 'service-package-card--has-compact' : ''}`}
        >
          {isCompact && (
            <button
              type="button"
              className="service-package-mobile-toggle"
              aria-expanded={packageExpanded}
              aria-controls="service-package-panel"
              id="service-package-toggle"
              onClick={() => setPackageExpanded((v) => !v)}
            >
              <div className="service-package-mobile-toggle-text">
                <span className="service-package-mobile-title">{pkg.title}</span>
                <span className="service-package-mobile-meta">{pkg.toggleMeta}</span>
              </div>
              <span className={`service-accordion-chevron ${packageExpanded ? 'is-open' : ''}`} aria-hidden />
            </button>
          )}
          <div
            id="service-package-panel"
            className={`service-package-panel ${isCompact && !packageExpanded ? 'is-collapsed' : ''}`}
            role={isCompact ? 'region' : undefined}
            aria-labelledby={isCompact ? 'service-package-toggle' : undefined}
          >
            <div className="service-package-panel-inner">
              {!isCompact && <div className="service-package-badge">{pkg.badge}</div>}
              {isCompact && packageExpanded && (
                <div className="service-package-badge service-package-badge--inline">{pkg.badge}</div>
              )}

              <div className="service-package-inner service-package-inner--unified">
                <div className="service-package-copy">
                  <h3 className="service-package-title">{pkg.title}</h3>
                  <p className="service-package-lead">{pkg.lead}</p>
                  <ul className="service-package-includes">
                    {pkg.includes.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                  <p className="service-package-promo">
                    <span className="service-package-promo-label">{pkg.promoLabel}</span>
                    {pkg.promo}
                  </p>
                  {pkg.liveHref && (
                    <a
                      className="service-package-live"
                      href={pkg.liveHref}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {pkg.liveLabel}
                    </a>
                  )}
                  <p className="service-package-note">{pkg.note}</p>
                </div>

                <div className="service-package-demo">
                  <h4 className="service-package-demo-title">{t('demo.formTitle')}</h4>
                  <form className="service-package-form" onSubmit={handleDemoSubmit}>
                    <div className="service-package-form-row">
                      <div className="service-package-field">
                        <label htmlFor="demo-name">{t('demo.fullName')}</label>
                        <input
                          id="demo-name"
                          name="name"
                          type="text"
                          autoComplete="name"
                          placeholder={t('demo.phName')}
                          required
                          disabled={demoSending}
                        />
                      </div>
                      <div className="service-package-field">
                        <label htmlFor="demo-email">{t('demo.email')}</label>
                        <input
                          id="demo-email"
                          name="email"
                          type="email"
                          autoComplete="email"
                          placeholder={t('demo.phEmail')}
                          required
                          disabled={demoSending}
                        />
                      </div>
                    </div>
                    <div className="service-package-form-row">
                      <div className="service-package-field">
                        <label htmlFor="demo-business">{t('demo.business')}</label>
                        <input
                          id="demo-business"
                          name="business"
                          type="text"
                          autoComplete="organization"
                          placeholder={t('demo.phBusiness')}
                          required
                          disabled={demoSending}
                        />
                      </div>
                      <div className="service-package-field">
                        <label htmlFor="demo-phone">{t('demo.phone')}</label>
                        <input
                          id="demo-phone"
                          name="phone"
                          type="tel"
                          autoComplete="tel"
                          placeholder={t('demo.phPhone')}
                          disabled={demoSending}
                        />
                      </div>
                    </div>
                    <div className="service-package-field">
                      <label htmlFor="demo-message">{t('demo.message')}</label>
                      <textarea
                        id="demo-message"
                        name="message"
                        rows={3}
                        placeholder={t('demo.phMessage')}
                        disabled={demoSending}
                      />
                    </div>
                    <button type="submit" className="service-package-submit" disabled={demoSending}>
                      {demoSending ? t('formStatus.sending') : t('demo.submit')}
                    </button>
                    <p className="service-package-form-note">{t('demo.note')}</p>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </article>
      </div>

      <div className="services-grid">
        {services.map((service, index) => {
          const expanded = !isCompact || serviceExpanded[index]
          const toggleId = `service-toggle-${index}`
          const panelId = `service-panel-${index}`
          return (
            <article
              key={service.title}
              data-card-index={index}
              className={`service-card ${visibleCards.includes(index) ? 'is-visible' : ''} ${
                isCompact ? 'service-card--has-compact' : ''
              }`}
              style={{ transitionDelay: `${index * 80}ms` }}
            >
              {isCompact && (
                <button
                  type="button"
                  className="service-card-mobile-toggle"
                  id={toggleId}
                  aria-expanded={!!serviceExpanded[index]}
                  aria-controls={panelId}
                  onClick={() =>
                    setServiceExpanded((prev) => ({
                      ...prev,
                      [index]: !prev[index],
                    }))
                  }
                >
                  <img src={service.image} alt="" className="service-card-mobile-thumb" width={56} height={56} />
                  <div className="service-card-mobile-info">
                    <span className="service-card-mobile-name">{service.title}</span>
                    <span className="service-card-mobile-type">{service.projectType}</span>
                  </div>
                  <span className={`service-accordion-chevron ${serviceExpanded[index] ? 'is-open' : ''}`} aria-hidden />
                </button>
              )}
              <div
                id={panelId}
                className={`service-card-panel ${isCompact && !expanded ? 'is-collapsed' : ''}`}
                role={isCompact ? 'region' : undefined}
                aria-labelledby={isCompact ? toggleId : undefined}
                hidden={isCompact && !expanded}
              >
                <div className="service-card-panel-inner">
                  <div className="service-media">
                    <img src={service.image} alt={service.title} />
                    <div className="service-media-overlay"></div>
                    <span className="service-type">{service.projectType}</span>
                  </div>
                  <div className="service-content">
                    <h3 className={isCompact ? 'service-sr-only' : undefined}>{service.title}</h3>
                    <p>{service.description}</p>
                    <div className="service-features">
                      {service.features.map((feature) => (
                        <span key={feature} className="feature-tag">
                          {feature}
                        </span>
                      ))}
                    </div>
                    <a href="#contact" className="service-cta">
                      {t('services.discussCta')}
                    </a>
                  </div>
                </div>
              </div>
            </article>
          )
        })}
      </div>

      <FormStatusModal status={demoStatus} onClose={() => setDemoStatus(null)} />
    </section>
  )
}

export default Services
